import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight, ArrowUpRight, Megaphone, Search, SearchX, X } from "lucide-react";
import { raiseBoardApi, type RaiseBoardSummary } from "@/api/raise-board-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  FundMark,
  PageContainer,
  HOUSE_COVER,
  PublicCover,
  PublicState,
  RaiseBoardNav,
  RaiseBoardShell,
  SectionHeading,
} from "./rb-ui";
import { BoardGrid, ViewToggle, useViewMode } from "./board-collection";
import { BoardCardSkeleton } from "./board-row";

/*
 * /rb — the Raiseboard directory.
 *
 * Every published board ranked by the three numbers a visitor actually cares
 * about: deals listed, upvotes earned, comments written. /global ranks
 * *companies* across all boards; this ranks the boards themselves, and is the
 * way into a specific /rb/:slug for someone who arrived without a link.
 */

type SortKey = "deals" | "upvotes" | "comments" | "recent";

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: "deals", label: "Deals" },
  { key: "upvotes", label: "Upvotes" },
  { key: "comments", label: "Comments" },
  { key: "recent", label: "Recently active" },
];

/** The figure each sort ranks on. "recent" ranks by date instead. */
const METRIC: Record<SortKey, ((b: RaiseBoardSummary) => number) | null> = {
  deals: (b) => b.deal_count,
  upvotes: (b) => b.upvote_count,
  comments: (b) => b.comment_count,
  recent: null,
};

function compactNumber(value: number): string {
  if (value < 1000) return String(value);
  return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}

function timeAgo(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * One figure in a board row's right-hand columns. Tabular digits so the
 * columns line up; the column being ranked on is ink, the rest recede.
 */
function BoardFigure({
  value,
  label,
  emphasis = false,
}: {
  value: number;
  label: string;
  emphasis?: boolean;
}) {
  return (
    <div className="w-[4.5rem] text-right">
      <p
        className={cn(
          "text-[19px] font-semibold leading-none tabular-nums tracking-[-0.02em] transition-colors duration-200",
          emphasis ? "text-foreground" : "text-muted-foreground/70"
        )}
      >
        {compactNumber(value)}
      </p>
      <p className="mt-1.5 text-[12px] text-muted-foreground">{label}</p>
    </div>
  );
}

function BoardIndexRow({
  board,
  rank,
  sort,
  index,
}: {
  board: RaiseBoardSummary;
  rank: number;
  sort: SortKey;
  index: number;
}) {
  const added = timeAgo(board.last_deal_added);

  return (
    <li
      className="group relative border-b border-border motion-safe:animate-rise"
      style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
    >
      <Link
        to={`/rb/${board.slug}`}
        aria-label={`Open the ${board.name} Raiseboard`}
        className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30"
      />

      <div className="pointer-events-none relative z-10 flex items-start gap-4 py-7 transition-transform duration-300 ease-out group-hover:translate-x-1.5 sm:gap-6">
        <span className="hidden w-10 shrink-0 pt-3.5 text-[13px] font-medium tabular-nums text-muted-foreground transition-colors duration-200 group-hover:text-foreground sm:block">
          {String(rank).padStart(2, "0")}
        </span>

        <FundMark
          name={board.name}
          logoUrl={board.logo_url}
          size="lg"
        />

        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-2 text-[20px] font-semibold leading-snug tracking-[-0.02em] sm:text-[21px]">
            <span className="truncate">{board.name}</span>
            <ArrowUpRight
              aria-hidden
              className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            />
          </h3>

          {board.description && (
            <p className="mt-1.5 line-clamp-2 max-w-[60ch] text-[15px] leading-relaxed text-muted-foreground">
              {plainText(board.description)}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            {added && <span>Newest deal {added}</span>}
            {board.global_deal_count > 0 && (
              <span>{board.global_deal_count} on the global board</span>
            )}
            <span className="text-muted-foreground/70">/rb/{board.slug}</span>
          </div>

          {/* Figures stack under the name on narrow screens, where the right
              columns have no room. */}
          <div className="mt-4 flex items-end gap-2 sm:hidden">
            <BoardFigure value={board.deal_count} label="Deals" emphasis={sort === "deals"} />
            <BoardFigure value={board.upvote_count} label="Upvotes" emphasis={sort === "upvotes"} />
            <BoardFigure value={board.comment_count} label="Comments" emphasis={sort === "comments"} />
          </div>
        </div>

        <div className="hidden shrink-0 items-start gap-3 pt-1.5 sm:flex">
          <BoardFigure value={board.deal_count} label="Deals" emphasis={sort === "deals"} />
          <BoardFigure value={board.upvote_count} label="Upvotes" emphasis={sort === "upvotes"} />
          <BoardFigure value={board.comment_count} label="Comments" emphasis={sort === "comments"} />
        </div>
      </div>
    </li>
  );
}

/** The grid view of one board: a ledger cell with the fund's mark leading. */
function BoardIndexCard({
  board,
  rank,
  sort,
  index,
}: {
  board: RaiseBoardSummary;
  rank: number;
  sort: SortKey;
  index: number;
}) {
  const added = timeAgo(board.last_deal_added);
  return (
    <li
      className="group relative bg-background transition-colors duration-200 hover:bg-card motion-safe:animate-rise"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <Link
        to={`/rb/${board.slug}`}
        aria-label={`Open the ${board.name} Raiseboard`}
        className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30"
      />
      <div className="pointer-events-none relative z-10 flex h-full min-h-[17rem] flex-col p-6">
        <div className="flex items-start justify-between gap-3">
          <FundMark name={board.name} logoUrl={board.logo_url} size="lg" />
          <span className="pt-1 text-[13px] font-medium tabular-nums text-muted-foreground transition-colors duration-200 group-hover:text-foreground">
            {String(rank).padStart(2, "0")}
          </span>
        </div>
        <h3 className="mt-5 flex items-center gap-2 text-[20px] font-semibold leading-snug tracking-[-0.02em]">
          <span className="truncate">{board.name}</span>
          <ArrowUpRight
            aria-hidden
            className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          />
        </h3>
        {board.description && (
          <p className="mt-2 line-clamp-3 text-[14.5px] leading-relaxed text-muted-foreground">
            {plainText(board.description)}
          </p>
        )}
        <div aria-hidden className="min-h-5 flex-1" />
        <div className="flex items-end justify-between gap-2 border-t border-border/80 pt-4">
          <BoardFigure value={board.deal_count} label="Deals" emphasis={sort === "deals"} />
          <BoardFigure value={board.upvote_count} label="Upvotes" emphasis={sort === "upvotes"} />
          <BoardFigure value={board.comment_count} label="Comments" emphasis={sort === "comments"} />
        </div>
        {added && (
          <p className="mt-3 text-[12.5px] text-muted-foreground">Newest deal {added}</p>
        )}
      </div>
    </li>
  );
}

function BoardIndexSkeleton() {
  return (
    <li className="flex items-start gap-4 border-b border-border py-7 sm:gap-6">
      <span className="hidden w-10 sm:block" />
      <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />
      <div className="flex-1 space-y-2.5 pt-1">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3.5 w-full max-w-[48ch]" />
        <Skeleton className="h-3 w-40" />
      </div>
      <Skeleton className="hidden h-9 w-52 sm:block" />
    </li>
  );
}

export default function RaiseBoardDirectory() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("deals");
  const [view, setView] = useViewMode("directory", "grid");
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["raise-boards"],
    queryFn: () => raiseBoardApi.getBoards(),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const boards = useMemo(() => data ?? [], [data]);

  const totals = useMemo(
    () =>
      boards.reduce(
        (acc, b) => ({
          deals: acc.deals + b.deal_count,
          upvotes: acc.upvotes + b.upvote_count,
          comments: acc.comments + b.comment_count,
        }),
        { deals: 0, upvotes: 0, comments: 0 }
      ),
    [boards]
  );

  const ranked = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? boards.filter(
          (b) =>
            b.name.toLowerCase().includes(needle) ||
            b.slug.toLowerCase().includes(needle) ||
            plainText(b.description ?? "").toLowerCase().includes(needle)
        )
      : boards;

    const metric = METRIC[sort];
    return [...matched].sort((a, b) => {
      if (!metric) {
        // Recently active: boards that have never dated a deal sort last.
        const at = a.last_deal_added ? Date.parse(a.last_deal_added) : 0;
        const bt = b.last_deal_added ? Date.parse(b.last_deal_added) : 0;
        return bt - at || b.deal_count - a.deal_count;
      }
      return metric(b) - metric(a) || b.deal_count - a.deal_count;
    });
  }, [boards, query, sort]);

  const authDialog = (
    <BoardAuthDialog
      open={!!authReason}
      onOpenChange={(open) => {
        if (!open) setAuthReason(null);
      }}
      reason={authReason ?? undefined}
    />
  );

  if (error) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav background={HOUSE_COVER} onRequestAuth={setAuthReason} />
        <PublicCover
          background={HOUSE_COVER}
          size="lg"
          eyebrow={
            <>
              <Megaphone />
              Boards
            </>
          }
          title="The board directory isn't available"
          lede="Something went wrong loading it. Please try again in a moment."
        />
        {authDialog}
      </RaiseBoardShell>
    );
  }

  const n = (value: number) => value.toLocaleString();

  return (
    <RaiseBoardShell footerFlush>
      <RaiseBoardNav background={HOUSE_COVER} onRequestAuth={setAuthReason} />

      <PublicCover
          background={HOUSE_COVER}
        eyebrow="Boards"
        title="Every Raiseboard"
        lede={
          isLoading ? (
            <Skeleton className="h-5 w-96 max-w-full" />
          ) : (
            // The totals are a sentence, not a stats band.
            <p role="group" aria-label="Raiseboard totals">
              <strong className="tabular-nums">{n(boards.length)}</strong>{" "}
              {boards.length === 1
                ? "fund, accelerator or community"
                : "funds, accelerators and communities"}{" "}
              publishing{" "}
              <strong className="tabular-nums">{n(totals.deals)}</strong>{" "}
              {totals.deals === 1 ? "deal" : "deals"}, with{" "}
              <strong className="tabular-nums">{n(totals.upvotes)}</strong>{" "}
              {totals.upvotes === 1 ? "upvote" : "upvotes"} and{" "}
              <strong className="tabular-nums">{n(totals.comments)}</strong>{" "}
              {totals.comments === 1 ? "comment" : "comments"} from the
              investors who follow them.
            </p>
          )
        }
        aside={
          <button
            type="button"
            onClick={() => navigate("/global")}
            className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-foreground underline-offset-4 transition-colors hover:underline"
          >
            Browse every deal on the global board
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        }
      />

      <PageContainer className="pt-10 sm:pt-12">
        <section aria-label="Boards">
          <SectionHeading
            title="The index"
            count={isLoading ? undefined : ranked.length}
            actions={
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <div className="relative w-full sm:w-56">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search boards"
                    aria-label="Search boards"
                    className="h-9 rounded-lg border-border bg-card pl-8 pr-8 text-[13px]"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div
                  role="group"
                  aria-label="Sort boards"
                  className="-mx-5 flex w-[calc(100%+2.5rem)] items-center gap-0.5 overflow-x-auto px-5 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:rounded-lg sm:border sm:border-border sm:bg-card sm:p-0.5"
                >
                  {SORTS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={sort === option.key}
                      onClick={() => setSort(option.key)}
                      className={cn(
                        "shrink-0 rounded-md px-2.5 py-1 text-[13px] transition-colors duration-150",
                        sort === option.key
                          ? "bg-foreground text-background"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <ViewToggle value={view} onChange={setView} />
              </div>
            }
          />

          {isLoading ? (
            view === "grid" ? (
              <BoardGrid>
                {[0, 1, 2].map((i) => (
                  <BoardCardSkeleton key={i} />
                ))}
              </BoardGrid>
            ) : (
              <ol className="border-t border-foreground">
                {[0, 1, 2, 3].map((i) => (
                  <BoardIndexSkeleton key={i} />
                ))}
              </ol>
            )
          ) : ranked.length === 0 ? (
            boards.length === 0 ? (
              <PublicState
                icon={<Megaphone />}
                title="No boards are publishing yet"
                className="border-t border-foreground"
              >
                When a fund turns on its Raiseboard, it shows up here.
              </PublicState>
            ) : (
              <PublicState
                icon={<SearchX />}
                title={`No boards match \u201c${query.trim()}\u201d`}
                className="border-t border-foreground"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-lg bg-card"
                    onClick={() => setQuery("")}
                  >
                    Clear search
                  </Button>
                }
              />
            )
          ) : view === "grid" ? (
            <BoardGrid>
              {ranked.map((board, index) => (
                <BoardIndexCard
                  key={board.slug}
                  board={board}
                  rank={index + 1}
                  sort={sort}
                  index={index}
                />
              ))}
            </BoardGrid>
          ) : (
            <ol className="border-t border-foreground">
              {ranked.map((board, index) => (
                <BoardIndexRow
                  key={board.slug}
                  board={board}
                  rank={index + 1}
                  sort={sort}
                  index={index}
                />
              ))}
            </ol>
          )}
        </section>
      </PageContainer>

      {/* The page's closing moment: a second, smaller ink band. */}
      <section className="ink-scope grain relative isolate mt-24 overflow-hidden bg-background [--grain-opacity:0.09]">
        <PageContainer className="flex flex-col gap-8 py-16 sm:flex-row sm:items-end sm:justify-between sm:py-20">
          <div>
            <p className="text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              For funds and accelerators
            </p>
            <h2 className="mt-4 text-[clamp(2rem,1.4rem+2.4vw,3.25rem)] font-semibold leading-[1] tracking-[-0.045em]">
              Run your own Raiseboard
            </h2>
            <p className="mt-4 max-w-[46ch] text-[17px] leading-relaxed text-muted-foreground">
              Publish the startups you back and let the investors who follow
              you endorse them.
            </p>
          </div>
          <Button
            onClick={() =>
              isSignedIn
                ? navigate("/workspaces")
                : setAuthReason("Sign in to run your own Raiseboard.")
            }
            className="h-10 shrink-0 gap-1.5 rounded-lg px-4 text-[13.5px]"
          >
            Get started free
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </PageContainer>
      </section>

      {authDialog}
    </RaiseBoardShell>
  );
}

/**
 * Board descriptions are written in the workspace's rich-text editor, so they
 * arrive as HTML. In a one-line index entry only the words matter: parse it
 * (never inject it) and keep the text.
 */
function plainText(html: string): string {
  if (typeof DOMParser === "undefined") return html.replace(/<[^>]*>/g, " ");
  // Block boundaries become spaces, or "Title</p><p>Body" reads "TitleBody".
  const spaced = html.replace(/<\/(p|li|div|h[1-6])>|<br\s*\/?>/gi, " ");
  const text = new DOMParser().parseFromString(spaced, "text/html").body.textContent ?? "";
  return text.replace(/\s+/g, " ").trim();
}
